import { FormElementType, PermissionRoles, type RecordState } from '@gearbelt/global';

export { FormElementType };

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  role: PermissionRoles | null;
  workspaces: Organisation[];
  createdAt: Date;
  timezone: string;
  sitemateUserId?: string;
}

export interface Organisation {
  id: string;
  name: string;
  logoUrl?: string;
  workspaceId: string;
  accountId: string;
  color: string;
  notifyInspection: boolean;
  featureAccess: {
    isContactsTabEnabled: boolean;
  };
  vertical?: string | null;
  role?: string | null;
}

export type Attachment = {
  id: string;
  label: string;
  createdAt: Date;
  updatedAt: Date;
  number: string;
  org: Organisation;
  user: User;
  serialNumber: string;
  manufacturer: string;
  registration: string;
  chassisVin: string;
  year: number;
  internalId: string;
  model: string;
  assetType: string;
  assetCategory: string;
  assetClass: string;
  comments: string;
};

export type Asset = {
  id: string;
  label: string;
  createdAt: Date;
  updatedAt: Date;
  number: string;
  org: Organisation;
  user: User;
  engineNumber: string;
  serialNumber: string;
  manufacturer: string;
  internalId: string;
  model: string;
  location?: Location;
  inspectionForm?: { id: string; name: string };
  serviceForm?: { id: string; name: string };
  issueForm?: { id: string; name: string };
  operator?: { id: string; name: string };
  year: number;
  chassisVin: string;
  registration: string;
  lastReadingHours: number;
  lastReadingMileage: number;
  type: { id: string; name: string };
  category: { id: string; name: string };
  comments: string;
  class: string;
  photoUrl?: string;
  thumbnailUrl?: string;
  /** Derived from schedule.mobile */
  mobile: boolean;
  checks: FormElement[];
  lastServiceDate: Date;
  lastServiceReading: number;
  lastServiceReadingHours: number;
  lastServiceReadingMileage: number;
  lastScheduleType: ScheduleType;
  servicing: boolean;
  servicePlan: ServicePlan;
  tags: Tag[];
  state: RecordState;
};

export type Tag = {
  id: string;
  tag: string;
};

export enum ScheduleType {
  Asset250 = 250,
  Asset500 = 500,
  Asset750 = 750,
  Asset1000 = 1000,
  Mobile10K = 10000,
  Mobile15K = 15000,
  Mobile20K = 20000,
  Mobile25K = 25000,
  Mobile30K = 30000,
}

export type ServicePlanTrigger = {
  id: string;
  coefficient: number;
  label: string;
};

export type ServicePlan = {
  id: string;
  name: string;
  triggers: ServicePlanTrigger[];
  default: boolean;
  org: Organisation;
  mobile: boolean;
  usageInterval: number;
  timeInterval: number;
  ref: string;
  total?: number;
  assets: Asset[];
};

export enum LocationStatus {
  Active,
  Inactive,
}

export type Location = {
  id: string;
  name: string;
  user: User;
  org: Organisation;
  assets?: number;
  status: LocationStatus;
  address: string;
  location: string;
  lat: number;
  lng: number;
  radius: number;
  placeId: string;
  zoom: number;
  emails?: string[];
};

export enum FormType {
  InspectionForm,
  ScheduleForm,
  RepairForm,
}

export enum FormCategory {
  InspectionEarthmoving,
  InspectionPilingRig,
  InspectionMiscellaneous,
  InspectionRoadVehicle,
  InspectionLightVehicle,
  ServiceDefault = 1000,
  IssueDefault = 2000,
  CustomForm = 9999,
}

export enum FormLayout {
  Single,
  Double,
  Triple,
  Quadruple,
}

export enum FormSubmissionState {
  Draft,
  Submitted,
  New,
}

export type Form = {
  id: string;
  ref?: string;
  org?: Organisation;
  elements: FormElement[];
  name: string;
  type: FormType;
  category: FormCategory;
  logo?: string;
  default: boolean;
  assets?: Asset[];
  total?: string;
};

export type FormElement = {
  id?: string;
  form?: Form;
  type: FormElementType;
  label: string;
  description: string;
  sequence: number;
  required: boolean;
  fixed: boolean;
  config?: Record<string, unknown>;
};

export type InspectionStatus = {
  type: { name: string };
  category: { name: string };
  label: string;
  lastReading: number;
  lastReadingAt: string;
  fromNow: string;
  mobile: boolean;
  status: 'success' | 'warning' | 'danger';
  id: string;
  servicing: boolean;
  operator?: string;
  location?: Location;
  tags: string[];
  lastReadingHours: number;
  lastReadingMileage: number;
  model?: string;
  class?: string;
  year?: number;
  registration?: string;
  serialNumber?: string;
  engineNumber?: string;
  chassisVin?: string;
  manufacturer?: string;
  internalId: string;
  lastServiceDate?: string;
  lastScheduleType?: string;
  lastServiceReading?: number;
  comments?: string | null;
};

export type PaginationOptions = {
  sort?: string;
  page?: number;
  limit: number;
  filter?: string[];
  group?: string;
  groupDir?: 'asc' | 'desc';
  dir?: 'asc' | 'desc';
  search?: string;
};

export type AssetType = {
  id: string;
  name: string;
  type: string;
  mobile: boolean;
};

export type AssetCategory = {
  id: string;
  name: string;
};

export enum ContactType {
  Workforce,
  Contractor,
  OEM,
}

export type Schedule = {
  id: string;
  name: string;
  triggers: ScheduleTrigger[];
  default: boolean;
  org: Organisation;
  mobile: boolean;
  usageInterval: number;
  timeInterval: number;
  ref: string;
  total?: number;
  assets: Asset[];
};

export type ScheduleTrigger = {
  id: string;
  coefficient: number;
  label: string;
};
